import React, { useEffect, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, DollarSign, MapPin, Clock, History, User, Phone, FileText, Calendar, StickyNote, Send } from 'lucide-react';
import { Loan } from './types';
import { CallOutcomeBadge } from './CallOutcomeBadge';
export type DetailTab = 'overview' | 'timeline' | 'notes';
interface LoanDetailModalProps {
  loan: Loan | null;
  initialTab?: DetailTab;
  onClose: () => void;
  onAddNote: (loanId: string, body: string) => void;
}
export function LoanDetailModal({
  loan,
  initialTab = 'overview',
  onClose,
  onAddNote
}: LoanDetailModalProps) {
  const [tab, setTab] = useState<DetailTab>(initialTab);
  const [draft, setDraft] = useState('');
  useEffect(() => {
    if (loan) {
      setTab(initialTab);
      setDraft('');
    }
  }, [loan, initialTab]);
  const submitNote = () => {
    if (!loan || !draft.trim()) return;
    onAddNote(loan.id, draft.trim());
    setDraft('');
  };
  return <AnimatePresence data-id="element-218">
      {loan && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm z-50" onClick={onClose} data-id="element-219" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.96,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.96,
        y: 20
      }} className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-2xl max-h-[85vh] overflow-hidden bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl z-50 flex flex-col" data-id="element-220">
            <div className="px-6 pt-6 pb-4 border-b border-slate-800 flex items-start justify-between" data-id="element-221">
              <div data-id="element-222">
                <p className="text-xs font-bold uppercase tracking-wider text-orange-400 mb-1" data-id="element-223">
                  Loan {loan.id}
                </p>
                <h2 className="text-2xl font-bold font-display text-white" data-id="element-224">
                  {loan.borrowerName}
                </h2>
                <p className="text-sm text-slate-400 mt-1" data-id="element-225">
                  {loan.stage} • {loan.state} • ${loan.amount.toLocaleString()}
                </p>
                <div className="mt-3 flex items-center gap-2" data-id="element-226">
                  <CallOutcomeBadge outcome={loan.lastCallOutcome} when={loan.lastCallAt} data-id="element-227" />
                </div>
              </div>
              <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white" data-id="element-228">
                <X className="w-5 h-5" data-id="element-229" />
              </button>
            </div>

            <div className="px-6 border-b border-slate-800 flex gap-1" data-id="element-230">
              {(['overview', 'timeline', 'notes'] as DetailTab[]).map(t => <button key={t} onClick={() => setTab(t)} className={`px-4 py-3 text-sm font-semibold capitalize border-b-2 transition-colors flex items-center gap-1.5 ${tab === t ? 'border-orange-500 text-white' : 'border-transparent text-slate-400 hover:text-slate-200'}`} data-id="element-231">
                  {t}
                  {t === 'notes' && loan.notes.length > 0 && <span className="text-[10px] font-bold bg-slate-800 text-slate-300 px-1.5 py-0.5 rounded-full" data-id="element-232">
                      {loan.notes.length}
                    </span>}
                </button>)}
            </div>

            <div className="overflow-y-auto p-6 flex-1" data-id="element-233">
              {tab === 'overview' && <div className="grid grid-cols-2 gap-4" data-id="element-234">
                  <Stat icon={DollarSign} label="Loan Amount" value={`$${loan.amount.toLocaleString()}`} data-id="element-235" />
                  <Stat icon={MapPin} label="Property State" value={loan.state} data-id="element-236" />
                  <Stat icon={FileText} label="Stage" value={loan.stage} data-id="element-237" />
                  <Stat icon={Clock} label="Since Last Call" value={`${loan.timeSinceLastCall}h`} data-id="element-238" />
                  <Stat icon={History} label="In Current Stage" value={`${loan.timeInStage}h`} data-id="element-239" />
                  <Stat icon={Phone} label="Call Attempts" value={String(loan.callAttempts)} data-id="element-240" />
                  <Stat icon={Calendar} label="Entered Shark Tank" value={new Date(loan.enteredSharkTankAt).toLocaleString()} data-id="element-241" />
                </div>}

              {tab === 'timeline' && <div className="space-y-4" data-id="element-242">
                  {buildTimeline(loan).map((evt, idx, arr) => <div key={idx} className="flex gap-4" data-id="element-243">
                      <div className="flex flex-col items-center" data-id="element-244">
                        <div className="w-3 h-3 rounded-full bg-orange-500 mt-1.5" data-id="element-245" />
                        {idx < arr.length - 1 && <div className="w-px flex-1 bg-slate-800 mt-1" data-id="element-246" />}
                      </div>
                      <div className="flex-1 pb-4" data-id="element-247">
                        <p className="text-sm font-semibold text-white" data-id="element-248">
                          {evt.title}
                        </p>
                        <p className="text-xs text-slate-400 mt-0.5" data-id="element-249">
                          {evt.detail}
                        </p>
                        <p className="text-[10px] text-slate-500 mt-1" data-id="element-250">
                          {evt.when}
                        </p>
                      </div>
                    </div>)}
                </div>}

              {tab === 'notes' && <div className="space-y-4" data-id="element-251">
                  <div className="rounded-xl border border-slate-800 bg-slate-950/40 p-3" data-id="element-252">
                    <textarea value={draft} onChange={e => setDraft(e.target.value)} onKeyDown={e => {
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') submitNote();
              }} placeholder="Add a note about this loan... (Cmd/Ctrl+Enter to submit)" className="w-full bg-transparent text-sm text-slate-200 placeholder:text-slate-500 resize-none focus:outline-none min-h-[72px]" data-id="element-253" />
                    <div className="flex justify-between items-center mt-2 pt-2 border-t border-slate-800" data-id="element-254">
                      <span className="text-[11px] text-slate-500" data-id="element-255">
                        Visible to all Loan Officers
                      </span>
                      <button onClick={submitNote} disabled={!draft.trim()} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-orange-500 hover:bg-orange-400 disabled:bg-slate-700 disabled:text-slate-500 disabled:cursor-not-allowed text-white text-xs font-bold transition-colors" data-id="element-256">
                        <Send className="w-3.5 h-3.5" data-id="element-257" />
                        Add Note
                      </button>
                    </div>
                  </div>

                  {loan.notes.length === 0 ? <div className="text-center py-8 text-slate-500 text-sm" data-id="element-258">
                      <StickyNote className="w-8 h-8 mx-auto mb-2 opacity-40" data-id="element-259" />
                      No notes yet — add the first one.
                    </div> : <div className="space-y-3" data-id="element-260">
                      {[...loan.notes].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()).map(n => <div key={n.id} className="rounded-xl border border-slate-800 bg-slate-950/30 p-3" data-id="element-261">
                            <div className="flex items-center justify-between mb-1" data-id="element-262">
                              <span className="text-sm font-semibold text-white" data-id="element-263">
                                {n.author}
                              </span>
                              <span className="text-[10px] text-slate-500" data-id="element-264">
                                {new Date(n.timestamp).toLocaleString()}
                              </span>
                            </div>
                            <p className="text-sm text-slate-300 whitespace-pre-wrap leading-relaxed" data-id="element-265">
                              {n.body}
                            </p>
                          </div>)}
                    </div>}
                </div>}
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}
function Stat({
  icon: Icon,
  label,
  value
}: {
  icon: ComponentType<{
    className?: string;
  }>;
  label: string;
  value: string;
}) {
  return <div className="rounded-lg border border-slate-800 bg-slate-950/50 p-3" data-id="element-266">
      <div className="flex items-center gap-1.5 text-slate-400 mb-1" data-id="element-267">
        <Icon className="w-3.5 h-3.5" data-id="element-268" />
        <span className="text-[10px] font-bold uppercase tracking-wider" data-id="element-269">
          {label}
        </span>
      </div>
      <p className="text-base font-semibold text-white" data-id="element-270">{value}</p>
    </div>;
}
function buildTimeline(loan: Loan) {
  const now = Date.now();
  return [{
    title: 'Application Taken',
    detail: `Submitted by ${loan.assignedLO}`,
    when: new Date(now - 1000 * 60 * 60 * (loan.timeInStage + 24)).toLocaleString()
  }, {
    title: `Moved to ${loan.stage}`,
    detail: 'Status updated in pipeline',
    when: new Date(now - 1000 * 60 * 60 * loan.timeInStage).toLocaleString()
  }, {
    title: 'Last Call Attempt',
    detail: `${loan.callAttempts} total attempts • ${loan.lastCallOutcome ?? 'no outcome logged'}`,
    when: new Date(now - 1000 * 60 * 60 * loan.timeSinceLastCall).toLocaleString()
  }, {
    title: 'Entered Shark Tank',
    detail: 'Loan flagged as neglected — open for claim',
    when: new Date(loan.enteredSharkTankAt).toLocaleString()
  }];
}